import type { KPIs, PriorityTiers, StoreMetrics } from "./schemas/reporting-period.js";

export type FindingSeverity = "blue" | "orange" | "green";

export interface Finding {
  severity: FindingSeverity;
  title: string;
  text: string;
}

function fmtPct(n: number): string {
  return `${n.toFixed(1)}%`;
}

function fmtInt(n: number): string {
  return new Intl.NumberFormat("en-AU", { maximumFractionDigits: 0 }).format(n);
}

export function autoFindings(
  kpis: KPIs,
  tiers: PriorityTiers,
  stores?: StoreMetrics[],
): Finding[] {
  const findings: Finding[] = [];

  if (kpis.extended_session_rate >= 40) {
    findings.push({
      severity: "orange",
      title: "Critical extended session rate",
      text: `${fmtPct(kpis.extended_session_rate)} of sessions exceeded 24 hours — above the 40% High Priority threshold.`,
    });
  } else if (kpis.extended_session_rate >= 25) {
    findings.push({
      severity: "orange",
      title: "Extended session rate on Watch List",
      text: `${fmtPct(kpis.extended_session_rate)} of sessions exceeded 24 hours — between the 25–40% Watch List range.`,
    });
  } else if (kpis.extended_session_rate < 10) {
    findings.push({
      severity: "green",
      title: "Excellent extended session rate",
      text: `${fmtPct(kpis.extended_session_rate)} extended session rate — below the 10% Excellent benchmark.`,
    });
  }

  if (kpis.fleet_idle_rate_pct >= 50) {
    findings.push({
      severity: "orange",
      title: "High fleet idle rate",
      text: `${fmtPct(kpis.fleet_idle_rate_pct)} of devices recorded zero sessions this period — worth reviewing which teams are carrying unused inventory.`,
    });
  }

  const highCount = tiers.high.length;
  if (highCount > 0) {
    findings.push({
      severity: "orange",
      title: `${highCount} team${highCount > 1 ? "s" : ""} flagged High Priority`,
      text: `${highCount === 1 ? "This team has" : "These teams have"} an extended rate or fleet idle rate above the High Priority threshold and require immediate attention.`,
    });
  }

  const excellentCount = tiers.excellent.length;
  if (excellentCount > 0) {
    findings.push({
      severity: "green",
      title: `${excellentCount} team${excellentCount > 1 ? "s" : ""} rated Excellent`,
      text: `${excellentCount === 1 ? "This team has" : "These teams have"} a low extended session rate and a low fleet idle rate.`,
    });
  }

  if (stores !== undefined) {
    const idleTotal = stores.reduce((acc, s) => acc + s.idle_devices, 0);
    if (idleTotal > 0) {
      const idleStores = stores.filter((s) => s.idle_devices > 0).length;
      findings.push({
        severity: "orange",
        title: `${idleTotal} idle device${idleTotal > 1 ? "s" : ""} across ${idleStores} team${idleStores > 1 ? "s" : ""}`,
        text: `Devices recorded zero sessions for the period. Review the No-Usage Devices sheet for the per-team breakdown.`,
      });
    }
  }

  if (findings.length === 0) {
    findings.push({
      severity: "blue",
      title: "Period processed successfully",
      text: `${fmtInt(kpis.total_sessions)} sessions across ${kpis.active_stores} active teams. Review individual tabs for detailed breakdowns.`,
    });
  }

  return findings;
}
